import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePlayerPopup } from '../contexts/PlayerPopupContext';
import { getRankInfo } from '../rank';

const API = process.env.REACT_APP_API_URL || '';

export default function PlayerPopup() {
  const { popup, closePopup } = usePlayerPopup();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Fetch profile when popup opens
  useEffect(() => {
    if (!popup) { setProfile(null); return; }
    setLoading(true);
    setProfile(null);
    axios.get(`${API}/api/user/${popup.userId}`)
      .then(r => setProfile(r.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [popup?.userId]); // eslint-disable-line

  // Position popup near click, avoid edges
  useEffect(() => {
    if (!popup) return;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const popupW = 260;
    const popupH = 300;
    const margin = 12;
    let left = popup.x + 12;
    let top  = popup.y - 10;
    if (left + popupW > W - margin) left = popup.x - popupW - 12;
    if (left < margin) left = margin;
    if (top + popupH > H - margin) top = H - popupH - margin;
    if (top < margin) top = margin;
    setPos({ top, left });
  }, [popup]);

  // Close on outside click
  const handleMouseDown = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target)) closePopup();
  }, [closePopup]);

  useEffect(() => {
    if (!popup) return;
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [popup, handleMouseDown]);

  if (!popup) return null;

  const rank = profile ? getRankInfo(profile.rating ?? 0) : null;
  const isMe = me && String(me.id) === String(popup.userId);
  const location = profile ? [
    profile.school_county ? profile.school_county.replace(/\b\w/g, c => c.toUpperCase()) : null,
    profile.school_state || null,
  ].filter(Boolean).join(', ') : '';

  const handleViewProfile = () => {
    closePopup();
    navigate(isMe ? '/profile' : `/user/${popup.userId}`);
  };

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="player-popup"
      style={{ top: pos.top, left: pos.left }}
    >
      {loading || !profile ? (
        <div className="player-popup-loading">
          <div className="queue-spinner" style={{ width: 22, height: 22 }} />
        </div>
      ) : (
        <>
          <div className="player-popup-header">
            <div className="player-popup-avatar">
              {profile.avatar
                ? <img src={profile.avatar} alt="" />
                : <div className="player-popup-avatar-initials">
                    {(profile.display_name || '?').charAt(0).toUpperCase()}
                  </div>
              }
            </div>
            <div className="player-popup-info">
              <div className="player-popup-name" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 2,
                  background: `${rank.color}18`, border: `1.2px solid ${rank.color}44`,
                  borderRadius: 5, padding: '1px 5px', fontSize: 11, fontWeight: 800,
                  color: rank.color, flexShrink: 0,
                }}>
                  {rank.level}
                  <img src={rank.icon} alt={rank.label} style={{ width: 14, height: 14, objectFit: 'contain' }} />
                </span>
                {profile.display_name}
              </div>
              <div className="player-popup-rank" style={{ color: rank.color }}>
                {rank.label}
              </div>
              {isMe && <div className="player-popup-elo">{profile.rating ?? 0} XP</div>}
            </div>
          </div>

          {isMe && (
            <div className="player-popup-stats">
              <div className="player-popup-stat">
                <div className="player-popup-stat-val">{profile.wins}</div>
                <div className="player-popup-stat-lbl">W</div>
              </div>
              <div className="player-popup-stat">
                <div className="player-popup-stat-val">{profile.losses}</div>
                <div className="player-popup-stat-lbl">L</div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginTop: 8, marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {profile.school && (
                <div style={{
                  display: 'inline-block', fontSize: 11, fontWeight: 600,
                  color: '#4338ca', background: '#eef2ff',
                  borderRadius: 10, padding: '2px 8px',
                  maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {profile.school}
                </div>
              )}
              {location && (
                <div style={{ fontSize: 10, color: 'var(--cb-gray-500)', marginTop: 5, paddingLeft: 2 }}>
                  {location}
                </div>
              )}
            </div>
            <div style={{
              flexShrink: 0, background: 'var(--cb-gray-100)',
              border: '1px solid var(--cb-gray-200)', borderRadius: 8,
              padding: '4px 10px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cb-navy)', lineHeight: 1.2 }}>{profile.login_streak ?? 0}</div>
              <div style={{ fontSize: 10, color: 'var(--cb-gray-500)', fontWeight: 500, marginTop: 1 }}>Login Streak</div>
            </div>
          </div>

          {profile.bio && (
            <div className="player-popup-bio">
              {profile.bio.length > 100 ? profile.bio.slice(0, 100) + '…' : profile.bio}
            </div>
          )}

          <button className="player-popup-view-btn" onClick={handleViewProfile}>
            View Full Profile →
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
